<template>
  <div class="call-log-reports-container">
    <CallLogFilters
      ref="filters"
      :callLogsData="callLogs"
      @filter-change="onFilterChange"
    />
    <div class="reports-wrap">
      <CallLogSummary :callLogs="filteredCallLogs" />
      <div class="pie-section-group">
        <div class="pie-section">
          <h5 class="chart-title">
            {{ $t('CALL_LOG_REPORT.CHART.BY_TYPE') }}
          </h5>
          <CallLogByTypePieChart :callLogs="filteredCallLogs" />
        </div>

        <div class="pie-section">
          <h5 class="chart-title">
            {{ $t('CALL_LOG_REPORT.CHART.BY_STATUS') }}
          </h5>
          <CallLogByStatusPieChart :callLogs="filteredCallLogs" />
        </div>

        <div class="pie-section">
          <h5 class="chart-title">
            {{ $t('CALL_LOG_REPORT.CHART.BY_DURATION') }}
          </h5>
          <CallLogByDurationPieChart :callLogs="filteredCallLogs" />
        </div>
      </div>
      <div class="bar-section">
        <h5 class="chart-title">
          {{ $t('CALL_LOG_REPORT.CHART.CALLS_BY_DATE') }}
        </h5>
        <CallLogByDateBarChart :callLogs="filteredCallLogs" />
      </div>
      <div class="table-section no-bg">
        <CallLogTable :callLogsData="filteredCallLogs" />
      </div>
    </div>
  </div>
</template>

<script>
import CallLogTable from './components/CallLogTable';
import CallLogFilters from './components/CallLogFilters';
import CallLogReportApi from '../../../../api/inbox/callLogReport';
import CallLogByTypePieChart from './components/CallLogByTypePieChart';
import CallLogByStatusPieChart from './components/CallLogByStatusPieChart';
import CallLogByDurationPieChart from './components/CallLogByDurationPieChart';
import CallLogByDateBarChart from './components/CallLogByDateBarChart';
import CallLogSummary from './components/CallLogSummary';

export default {
  name: 'CallLogReportsView',
  components: {
    CallLogFilters,
    CallLogTable,
    CallLogSummary,
    CallLogByTypePieChart,
    CallLogByStatusPieChart,
    CallLogByDurationPieChart,
    CallLogByDateBarChart,
  },
  data() {
    return {
      callLogs: [],
      filters: {
        customDateRange: '',
        inboxes: [],
        types: [],
        statuses: [],
        durations: [],
      },
    };
  },
  computed: {
    filteredCallLogs() {
      return this.callLogs;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.filters.emitFilters();
    });
  },
  methods: {
    prepareCallLogs(rawCallLogs) {
      if (!Array.isArray(rawCallLogs)) return [];

      return rawCallLogs.map(callLog => {
        const statusMap = {
          completed: this.$t('CALL_LOG_REPORT.STATUS.COMPLETED'),
          failed: this.$t('CALL_LOG_REPORT.STATUS.FAILED'),
        };

        const typeMap = {
          inbound: this.$t('CALL_LOG_REPORT.TYPE.INBOUND'),
          outbound: this.$t('CALL_LOG_REPORT.TYPE.OUTBOUND'),
        };

        const status_message =
          statusMap[callLog.call_status] || this.$t('CALL_LOG_REPORT.UNKNOWN');

        const type_message =
          typeMap[callLog.call_type] || this.$t('CALL_LOG_REPORT.UNKNOWN');

        const formatDuration = (seconds) => {
          if (!seconds) return '0s';
          const mins = Math.floor(seconds / 60);
          const secs = seconds % 60;
          return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
        };

        const duration_formatted = formatDuration(callLog.duration);

        // Definir faixa de duração
        let duration_range = 'short';
        if (callLog.duration > 300) duration_range = 'long';
        else if (callLog.duration > 60) duration_range = 'medium';

        const link_conversation = `${window.location.origin}/app/accounts/${callLog.account_id}/conversations/${callLog.display_id}`;

        return {
          ...callLog,
          status_message,
          type_message,
          duration_formatted,
          duration_range,
          link_conversation,
        };
      });
    },
    async onFilterChange(newFilters) {
      this.filters = {
        ...this.filters,
        ...newFilters,
      };

      try {
        const [start, end] = this.filters.customDateRange || [];
        const formatDate = date => new Date(date).toISOString().split('T')[0];

        const inboxIds = this.filters.inboxes;
        const types = this.filters.types;
        const statuses = this.filters.statuses;
        const durations = this.filters.durations;

        const response = await CallLogReportApi.getAll({
          start_date: start ? formatDate(start) : null,
          end_date: end ? formatDate(end) : null,
          inboxes: inboxIds,
          types: types,
          statuses: statuses,
          durations: durations,
        });

        const callLogs = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];

        this.callLogs = this.prepareCallLogs(callLogs);
      } catch (error) {
        console.error(this.$t('CALL_LOG_REPORT.ERRORS.FETCH'), error);
      }
    },
  },
};
</script>

<style scoped>
.call-log-reports-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  align-items: flex-start;
}

.call-log-reports-container > *:first-child {
  width: 280px;
  flex-shrink: 0;
  margin-right: 24px;
}

.reports-wrap {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 2vh 2vw 10vh;
  background: #f8fafc;
  overflow: hidden;
  min-width: 0;
  box-sizing: border-box;
}

.pie-section,
.bar-section,
.table-section {
  margin-top: 4vh;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  width: 100%;
  overflow-x: auto;
}

.table-section.no-bg {
  background: transparent;
  padding: 0;
  box-shadow: none;
}

.pie-section-group {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 1vh;
}

.pie-section-group .pie-section {
  flex: 1 1 30%;
  max-height: 400px;
  overflow: hidden;
  background: #fff;
  padding: 1rem;
  border-radius: 10px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.05);
}

.chart-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 12px;
}
</style>